import React, {createContext, useContext, useEffect, useState} from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import {jwtDecode} from "jwt-decode";
import Parse, { SUPER_ADMIN_EMAIL, setReadOnly } from '../api/config';

// Kept in step with ALLOWED_DOMAINS / LEGACY_EMAILS in cloud/admin-management.js,
// which is where this is actually enforced. This check only exists so people
// get a clear message instead of a failed save.
export const ALLOWED_DOMAINS = ['bethelchristianschools.com', 'bethelchristiancenter.com'];
const LEGACY_EMAILS = ['timroejr@msn.com'];

const isBethelEmail = (email) => {
    const normalized = (email || '').trim().toLowerCase();
    if (LEGACY_EMAILS.includes(normalized)) return true;
    return ALLOWED_DOMAINS.includes(normalized.split('@').pop());
};

// Whether an error means the session itself is dead — as opposed to the server
// being briefly unreachable. Only a real 209 (or that message) should sign
// someone out. A 502/network drop returns HTML or nothing ("invalid JSON"),
// which means "can't reach the server", NOT "your session ended" — signing out
// over that would kick everyone out every time the server restarts.
const isInvalidSession = (error) => {
    if (!error) return false;
    if (error.code === Parse.Error.INVALID_SESSION_TOKEN) return true; // 209
    const message = (error.message || '').toLowerCase();
    return message.includes('invalid session token') || message.includes('session token is expired');
};

const AuthContext = createContext({
    user: null,
    realUser: null,
    parseUser: null,
    authError: null,
    isImpersonating: false,
    startImpersonation: () => {},
    stopImpersonation: () => {},
    signIn: () => {},
    signOut: () => {}
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [parseUser, setParseUser] = useState(null);
    const [authError, setAuthError] = useState(null);

    // "View as another user": the app renders as `impersonated`, but the real
    // Parse session stays this admin's. It lives in memory only — a refresh
    // ends it — and it flips the SDK into read-only so nothing can be written
    // while it's on. See setReadOnly in api/config.js.
    const [impersonated, setImpersonated] = useState(null);

    const stopImpersonation = () => {
        setImpersonated(null);
        setReadOnly(false);
    };

    // Only the permanent global administrator can do this, and never onto
    // themselves. It's read-only, so it grants no ability the admin's session
    // didn't already have — it just changes whose view is shown.
    const startImpersonation = (target) => {
        if (!target?.email) return;
        if ((user?.email || '').toLowerCase() !== SUPER_ADMIN_EMAIL) return;
        if (target.email.toLowerCase() === SUPER_ADMIN_EMAIL) return;
        setReadOnly(true);
        setImpersonated({ name: target.name || target.email, email: target.email, picture: target.picture });
    };

    const clearStoredSession = () => {
        stopImpersonation();
        setUser(null);
        setParseUser(null);
        localStorage.removeItem('user');
        localStorage.removeItem('parseUser');
    };

    const endExpiredSession = async () => {
        await Parse.User.logOut().catch(() => {});
        clearStoredSession();
        setAuthError('Your session has expired. Please sign in again.');
    };

    // Ask the server whether the token is still good.
    //   'valid'   - confirmed
    //   'expired' - the server rejected the token (209) -> sign out
    //   'unknown' - couldn't reach the server (502/offline) -> leave as-is
    const checkSession = async () => {
        try {
            await Parse.Session.current();
            return 'valid';
        } catch (error) {
            if (isInvalidSession(error)) return 'expired';
            console.warn('[auth] Could not reach the server to verify your session; staying signed in.', error.message);
            return 'unknown';
        }
    };

    // The Parse session is the source of truth, not the Google profile we
    // cached. Without a live Parse session the SDK sends no session token, so
    // request.user is null server-side and every role-aware cloud function
    // fails — while the app would happily render as if you were signed in.
    useEffect(() => {
        const storedUser = localStorage.getItem('user');
        if (!storedUser) return;

        (async () => {
            const currentUser = await Parse.User.currentAsync();

            if (!currentUser || !currentUser.getSessionToken()) {
                console.warn('[auth] No Parse session for the stored profile — signing out.');
                clearStoredSession();
                setAuthError('Your session has expired. Please sign in again.');
                return;
            }

            // Only a genuine rejection signs you out. If the server is briefly
            // down at load, keep you signed in — the periodic check below will
            // confirm or expire the session once the server answers.
            if ((await checkSession()) === 'expired') {
                await endExpiredSession();
                return;
            }

            setUser(JSON.parse(storedUser));
            setParseUser(currentUser);
        })();
        // Runs once on mount; the helpers it calls are stable enough for this.
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Sessions can expire or be revoked while the app is open, so re-check
    // instead of trusting the one check at load. On a real expiry this signs
    // you out automatically; a server hiccup is ignored. Checking when the tab
    // regains focus catches the common case of coming back after a long while.
    useEffect(() => {
        if (!user) return;

        let stopped = false;
        const revalidate = async () => {
            if (stopped) return;
            if ((await checkSession()) === 'expired' && !stopped) {
                await endExpiredSession();
            }
        };

        const interval = setInterval(revalidate, 5 * 60 * 1000);
        const onVisible = () => {
            if (document.visibilityState === 'visible') revalidate();
        };
        window.addEventListener('focus', revalidate);
        document.addEventListener('visibilitychange', onVisible);

        return () => {
            stopped = true;
            clearInterval(interval);
            window.removeEventListener('focus', revalidate);
            document.removeEventListener('visibilitychange', onVisible);
        };
    }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

    const signIn = async (response) => {
        if (response.credential) {
            const userPayload = jwtDecode(response.credential);

            if (!isBethelEmail(userPayload.email)) {
                setAuthError(
                    `Bethel Event Forms is for Bethel staff. Sign in with your ${ALLOWED_DOMAINS.map((d) => `@${d}`).join(' or ')} account — ${userPayload.email} can't be used.`
                );
                return;
            }
            setAuthError(null);

            const userData = {
                name: userPayload.name,
                email: userPayload.email,
                picture: userPayload.picture
            };

            try {
                // Authenticate with Parse using Google credentials
                const parseUser = await Parse.User.logInWith('google', {
                    authData: {
                        id: userPayload.sub,
                        id_token: response.credential
                    }
                });
                
                // No session token means no request.user server-side, which
                // silently breaks approvals and the admin check. Fail here
                // instead of letting the app look signed in.
                if (!parseUser.getSessionToken()) {
                    throw new Error('Parse did not return a session. Sign in again, or contact IT.');
                }

                // The email has to land on the Parse user: invitations, roles and
                // form ownership are all keyed off it.
                if (!parseUser.get('name') || !parseUser.get('email')) {
                    parseUser.set('name', userPayload.name);
                    parseUser.set('email', userPayload.email);
                    await parseUser.save();
                }

                setUser(userData);
                setParseUser(parseUser);

                localStorage.setItem('user', JSON.stringify(userData));
                localStorage.setItem('parseUser', 'true'); // Just a flag, we'll use Parse.User.current()
            } catch (error) {
                console.error('Parse authentication error:', error);
                setAuthError(error.message || 'Sign-in failed. Try again, or contact IT.');
            }
        }
    };

    const signOut = async () => {
        try {
            await Parse.User.logOut();
        } catch (error) {
            console.error('Error during sign out:', error);
        } finally {
            // Clear locally even if the server call failed, or the app would
            // stay stuck in a half-signed-in state.
            clearStoredSession();
        }
    };

    return (
        <AuthContext.Provider
            value={{
                // The identity the rest of the app reads from — the impersonated
                // user when viewing as someone, otherwise the signed-in admin.
                user: impersonated || user,
                realUser: user,
                parseUser,
                authError,
                isImpersonating: !!impersonated,
                startImpersonation,
                stopImpersonation,
                signIn,
                signOut,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

const clientId = "273944291151-i9cmblf29188vhb7csmggrnikp9i6j29.apps.googleusercontent.com";

export const AuthProviderWrapper = ({ children }) => (
    <GoogleOAuthProvider clientId={clientId}>
        <AuthProvider>
            {children}
        </AuthProvider>
    </GoogleOAuthProvider>
);
